import { useMutation, useQueryClient } from '@tanstack/react-query'
import { createApiCall } from 'services'
import type { Profile, ThemePreference } from 'types'
import { USER_PROFILE_QUERY_KEY } from './useUserProfileQuery'
import { WORKOS_PROFILE_QUERY_KEY } from '../useWorkOSAuth'

type LogoUpload = {
  file: File | null
  preview_url: string
  isUploaded: boolean
}

type UpdateUserProfileInput = {
  first_name?: string
  last_name?: string
  phone?: string
  avatar?: LogoUpload
  title?: string
  department?: string
  theme?: ThemePreference
  notification_settings?: Record<string, { platform: boolean; email: boolean }>
}

type UseUserProfileMutationsResult = {
  update: (payload: UpdateUserProfileInput) => void
  updateAsync: (payload: UpdateUserProfileInput) => Promise<Profile>
  removeAvatar: () => void
  removeAvatarAsync: () => Promise<Profile>
  isUpdating: boolean
  isRemovingAvatar: boolean
  isError: boolean
}

const removeEmptyValues = (payload: Record<string, unknown>) => {
  const cleanedPayload = { ...payload }

  Object.keys(cleanedPayload).forEach((key) => {
    if (
      cleanedPayload[key] === undefined ||
      cleanedPayload[key] === ''
    ) {
      delete cleanedPayload[key]
    }
  })

  return cleanedPayload
}

export const useUserProfileMutations = (): UseUserProfileMutationsResult => {
  const queryClient = useQueryClient()
  const invalidateProfileQueries = () => {
    queryClient.invalidateQueries({ queryKey: USER_PROFILE_QUERY_KEY })
  }

  // The sidebar (SidebarUserBlock) reads avatar + name from the WorkOS profile
  // cache (useWorkOSAuth → GET /auth/profile), a different endpoint than the
  // PUT /auth/admin/profile we just saved — so a refetch of it can still return
  // the stale avatar. Patch that cache directly from the authoritative mutation
  // response so the sidebar updates immediately, and do NOT invalidate it (a
  // refetch of the lagging endpoint would clobber this patch).
  const syncWorkosProfile = (data: Profile | undefined | null) => {
    // Guard: if the mutation response has no body (e.g. an empty 200/204, or a
    // flaky API), don't crash on data.first_name — the invalidate below refetches
    // the real profile anyway.
    if (!data) return
    queryClient.setQueryData(WORKOS_PROFILE_QUERY_KEY, (old: any) => {
      if (!old) return old
      const firstName = data.first_name ?? old.firstName
      const lastName = data.last_name ?? old.lastName
      return {
        ...old,
        avatar: data.avatar ?? null,
        firstName,
        lastName,
        fullName: [firstName, lastName].filter(Boolean).join(' ') || old.fullName,
        email: data.email ?? old.email,
      }
    })
  }

  const updateMutation = useMutation({
    mutationFn: async (payload: UpdateUserProfileInput) => {
      const { avatar, ...rest } = payload

      // Always use multipart/form-data when avatar is present (even if already uploaded)
      // This matches the pattern used in company/owner creation
      const hasAvatar = avatar && (avatar.file || (avatar.isUploaded && avatar.preview_url))

      if (hasAvatar) {
        const formData = new FormData()

        // Add profile data as stringified JSON
        const profileData = removeEmptyValues({ ...rest })

        // If avatar is already uploaded, include its path in the data
        // This allows the backend to keep the existing avatar if no new file is uploaded
        if (avatar.isUploaded && avatar.preview_url) {
          profileData.avatar = avatar.preview_url
        }

        formData.append('data', JSON.stringify(profileData))

        // Add avatar file if it's a new upload
        if (avatar.file) {
          formData.append('file', avatar.file)
        }

        const response = await createApiCall({
          method: 'PUT',
          url: '/auth/admin/profile',
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          data: formData,
        })

        return response.data as Profile
      } else {
        // Regular JSON request if no avatar
        const profileData = removeEmptyValues({ ...rest })

        const response = await createApiCall({
          method: 'PUT',
          url: '/auth/admin/profile',
          data: profileData,
        })

        return response.data as Profile
      }
    },
    // Optimistically reflect the picked avatar in the sidebar the moment Save is
    // pressed, so it doesn't lag behind the upload round-trip; reconcile on
    // success, roll back on error.
    onMutate: async (payload: UpdateUserProfileInput) => {
      const preview = payload.avatar?.preview_url
      if (preview === undefined) return {}
      const previousWorkos = queryClient.getQueryData(WORKOS_PROFILE_QUERY_KEY)
      queryClient.setQueryData(WORKOS_PROFILE_QUERY_KEY, (old: any) =>
        old ? { ...old, avatar: preview || null } : old,
      )
      return { previousWorkos }
    },
    onError: (_err, _payload, context: any) => {
      if (context && 'previousWorkos' in context) {
        queryClient.setQueryData(WORKOS_PROFILE_QUERY_KEY, context.previousWorkos)
      }
    },
    onSuccess: (data) => {
      invalidateProfileQueries()
      syncWorkosProfile(data)
    },
  })

  const removeAvatarMutation = useMutation({
    mutationFn: async () => {
      const response = await createApiCall({
        method: 'DELETE',
        url: '/auth/admin/avatar',
      })

      return response.data as Profile
    },
    // Clear the sidebar avatar immediately on Remove; reconcile on success,
    // roll back on error.
    onMutate: async () => {
      const previousWorkos = queryClient.getQueryData(WORKOS_PROFILE_QUERY_KEY)
      queryClient.setQueryData(WORKOS_PROFILE_QUERY_KEY, (old: any) =>
        old ? { ...old, avatar: null } : old,
      )
      return { previousWorkos }
    },
    onError: (_err, _vars, context: any) => {
      if (context && 'previousWorkos' in context) {
        queryClient.setQueryData(WORKOS_PROFILE_QUERY_KEY, context.previousWorkos)
      }
    },
    onSuccess: (data) => {
      invalidateProfileQueries()
      syncWorkosProfile(data)
    },
  })

  const isError = updateMutation.isError || removeAvatarMutation.isError

  return {
    update: updateMutation.mutate,
    updateAsync: updateMutation.mutateAsync,
    removeAvatar: removeAvatarMutation.mutate,
    removeAvatarAsync: removeAvatarMutation.mutateAsync,
    isUpdating: updateMutation.isPending,
    isRemovingAvatar: removeAvatarMutation.isPending,
    isError,
  }
}
